
// AgentAssignmentAccept.jsx
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,TextField
} from '@mui/material';
import { CheckCircle, Close, LocalShipping } from '@mui/icons-material';
import { orderAssignmentAPI } from './deliveryAgentAPI';

const AgentAssignmentAccept = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    if (!token) {
      setError('Invalid assignment link');
      return;
    }

    try {
      setLoading(true);
      const response = await orderAssignmentAPI.acceptAssignment(token);

      if (response.data.success) {
        setSuccess(true);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to accept assignment');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error accepting assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {!success && !error && (
            <>
              <Box textAlign="center" mb={3}>
                <LocalShipping sx={{ fontSize: 60, color: '#006D77', mb: 2 }} />
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Accept Assignment
                </Typography>
                <Typography color="text.secondary">
                  Click the button below to accept this delivery assignment
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Alert severity="info" sx={{ mb: 3 }}>
                By accepting this assignment, you will receive customer details and
                order information via email.
              </Alert>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                onClick={handleAccept}
                disabled={loading}
                sx={{
                  bgcolor: '#28a745',
                  '&:hover': { bgcolor: '#218838' },
                  py: 1.5
                }}
              >
                {loading ? 'Processing...' : 'Accept Assignment'}
              </Button>
            </>
          )}

          {success && (
            <Box textAlign="center">
              <CheckCircle sx={{ fontSize: 80, color: '#28a745', mb: 2 }} />
              <Typography variant="h4" fontWeight={600} gutterBottom color="#28a745">
                Assignment Accepted!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Customer details and delivery instructions have been sent to your email.
                Please check your inbox for further information.
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                <strong>Next Steps:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '20px', textAlign: 'left' }}>
                  <li>Check your email for customer details</li>
                  <li>Contact the customer before visiting</li>
                  <li>Use the action buttons in email to update order status</li>
                </ul>
              </Alert>
            </Box>
          )}

          {error && (
            <Box textAlign="center">
              <Close sx={{ fontSize: 80, color: '#dc3545', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom color="#dc3545">
                Assignment Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="outlined"
                onClick={() => window.close()}
                sx={{ mt: 2 }}
              >
                Close Window
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// AgentAssignmentReject.jsx
const AgentAssignmentReject = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');

  const handleReject = async () => {
    if (!token) {
      setError('Invalid assignment link');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setLoading(true);
      const response = await orderAssignmentAPI.rejectAssignment(token, reason);

      if (response.data.success) {
        setSuccess(true);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to reject assignment');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error rejecting assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {!success && !error && (
            <>
              <Box textAlign="center" mb={3}>
                <Close sx={{ fontSize: 60, color: '#dc3545', mb: 2 }} />
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Reject Assignment
                </Typography>
                <Typography color="text.secondary">
                  Please provide a reason for rejecting this assignment
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason for Rejection"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="E.g., Not available, Vehicle breakdown, etc."
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Close />}
                onClick={handleReject}
                disabled={loading}
                sx={{
                  bgcolor: '#dc3545',
                  '&:hover': { bgcolor: '#c82333' },
                  py: 1.5
                }}
              >
                {loading ? 'Processing...' : 'Confirm Rejection'}
              </Button>
            </>
          )}

          {success && (
            <Box textAlign="center">
              <CheckCircle sx={{ fontSize: 80, color: '#28a745', mb: 2 }} />
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Assignment Rejected
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                The assignment has been rejected and will be reassigned to another agent.
              </Typography>
              <Alert severity="info">
                Thank you for your response. The order will be assigned to another available agent.
              </Alert>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// AgentOrderPickup.jsx
const AgentOrderPickup = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirmPickup = async () => {
    if (!token) {
      setError('Invalid confirmation link');
      return;
    }

    try {
      setLoading(true);
      const response = await orderAssignmentAPI.confirmPickup(token);

      if (response.data.success) {
        setSuccess(true);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to confirm pickup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error confirming pickup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {!success && !error && (
            <>
              <Box textAlign="center" mb={3}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#007bff20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}
                >
                  <Typography fontSize={40}>📦</Typography>
                </Box>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Confirm Order Pickup
                </Typography>
                <Typography color="text.secondary">
                  Click the button below to confirm that you have picked up the order
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Before confirming:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                  <li>Ensure you have collected all items</li>
                  <li>Verify the order details</li>
                  <li>Check the delivery address</li>
                </ul>
              </Alert>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                onClick={handleConfirmPickup}
                disabled={loading}
                sx={{
                  bgcolor: '#007bff',
                  '&:hover': { bgcolor: '#0056b3' },
                  py: 1.5
                }}
              >
                {loading ? 'Processing...' : '✓ Confirm Pickup'}
              </Button>
            </>
          )}

          {success && (
            <Box textAlign="center">
              <CheckCircle sx={{ fontSize: 80, color: '#28a745', mb: 2 }} />
              <Typography variant="h4" fontWeight={600} gutterBottom color="#28a745">
                Pickup Confirmed!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Order status has been updated to "Picked Up". Please proceed with the delivery.
              </Typography>
              <Alert severity="success">
                A confirmation email has been sent to you. Safe travels! 🚗
              </Alert>
            </Box>
          )}

          {error && (
            <Box textAlign="center">
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="outlined"
                onClick={() => window.close()}
                sx={{ mt: 2 }}
              >
                Close Window
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// AgentOrderDeliver.jsx
const AgentOrderDeliver = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirmDelivery = async () => {
    if (!token) {
      setError('Invalid confirmation link');
      return;
    }

    try {
      setLoading(true);
      const response = await orderAssignmentAPI.confirmDelivery(token);

      if (response.data.success) {
        setSuccess(true);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to confirm delivery');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error confirming delivery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {!success && !error && (
            <>
              <Box textAlign="center" mb={3}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#28a74520',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}
                >
                  <Typography fontSize={40}>✓</Typography>
                </Box>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Confirm Order Delivery
                </Typography>
                <Typography color="text.secondary">
                  Click the button below to confirm successful delivery
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Before confirming:</strong>
                <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                  <li>Ensure customer has received all items</li>
                  <li>Collect payment if COD</li>
                  <li>Get customer signature/confirmation if required</li>
                </ul>
              </Alert>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                onClick={handleConfirmDelivery}
                disabled={loading}
                sx={{
                  bgcolor: '#28a745',
                  '&:hover': { bgcolor: '#218838' },
                  py: 1.5
                }}
              >
                {loading ? 'Processing...' : '✓ Confirm Delivery'}
              </Button>
            </>
          )}

          {success && (
            <Box textAlign="center">
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: '#28a74520',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}
              >
                <Typography fontSize={60}>🎉</Typography>
              </Box>
              <Typography variant="h3" fontWeight={600} gutterBottom color="#28a745">
                Delivery Completed!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Great job! The order has been successfully delivered.
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                <strong>Order Status Updated:</strong> The order has been marked as delivered 
                and your delivery count has been updated.
              </Alert>
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#e7f3ff',
                  borderRadius: 2,
                  mb: 2
                }}
              >
                <Typography variant="h6" gutterBottom>
                  🌟 Thank you for your excellent service!
                </Typography>
                <Typography color="text.secondary">
                  A confirmation email has been sent to you with delivery details.
                </Typography>
              </Box>
            </Box>
          )}

          {error && (
            <Box textAlign="center">
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="outlined"
                onClick={() => window.close()}
                sx={{ mt: 2 }}
              >
                Close Window
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export {
  AgentAssignmentAccept,
  AgentAssignmentReject,
  AgentOrderPickup,
  AgentOrderDeliver
};